import { Group, Stack } from '@mantine/core';
import { PortalButton } from 'components/Button/PortalButton';
import { CreateRuleFormProvider, useCreateRuleForm } from 'context/FormContext';
import { useDrawerContext } from 'context/hooks';
import { RuleItem, RuleStatus, RuleStatuses } from 'models/rules';
import { useTranslation } from 'react-i18next';
import { BsFloppy, BsSearch } from 'react-icons/bs';
import { useEditRule } from 'hooks/api/rule/useEditRule';
import { BasicInfoFields } from './DefineRule/Fields/BasicInfoFields';
import { RuleConfigurationFields } from './DefineRule/Fields/RuleConfigurationFields';
import { DefineConditionsFields } from './DefineRule/Fields/DefineConditionsFields';
import { useDefineRuleFormValidations } from './DefineRule/hooks/useDefineRuleFormValidations';

interface Props {
  item: RuleItem;
}

export const DraftRuleForm = ({ item }: Props) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { validate, setFormValues } = useDefineRuleFormValidations();
  const form = useCreateRuleForm({
    initialValues: setFormValues({ item }),
    validate,
  });
  const { mutate: editRule } = useEditRule({ reset: () => form.setFieldValue('formDisabled', false) });
  const { values } = form;

  const handleDrawerClose = () => {
    form.reset();
    closeDrawer();
  };

  const handleFormSave = (status: RuleStatus) => {
    form.validate();
    if (form.isValid()) {
      form.setFieldValue('formDisabled', true);
      editRule({ ...values, patientId: item.patientId, groupId: item.groupId, toStatus: status, id: item.id });
    }
  };

  return (
    <CreateRuleFormProvider form={form}>
      <Stack>
        <BasicInfoFields />
        <RuleConfigurationFields />
        <DefineConditionsFields />
        <Group mt={'xl'} justify={'flex-start'}>
          <PortalButton
            label={t('review_label')}
            Icon={BsSearch}
            disabled={values.formDisabled}
            onClick={() => handleFormSave(RuleStatuses.inReview)}
          />
          <PortalButton
            label={t('save_label')}
            Icon={BsFloppy}
            disabled={values.formDisabled}
            variant={'light'}
            onClick={() => handleFormSave(RuleStatuses.draft)}
          />
          <PortalButton
            variant={'outline'}
            onClick={handleDrawerClose}
            label={t('cancel_label')}
            disabled={values.formDisabled}
          />
        </Group>
      </Stack>
    </CreateRuleFormProvider>
  );
};
